import type { ReactNode } from "react";
import {
  planAngleDeg,
  planCentroid,
  planDistanceM,
  planPathLengthM,
  planPolygonAreaM2,
  planPolygonSelfIntersects,
  type PlanPoint,
} from "@sts/geometry";
import { formatArea, formatLength } from "@sts/scan-viewer";
import { toView, type PlanFit } from "./plan-view.js";
import { midpoint, round, uprightAngle } from "./plan-marks.jsx";
import { kindOf, pointsOf, type PlanDimension } from "./plan-annotations.js";

/**
 * The two measurements added beside the plain length on 2026-09-11: an angle
 * (two legs from one corner, each with its length, and the angle between
 * them) and an area (a closed run of points, and the square metres inside
 * it). Drawn in the markup colour with the figure in a filled pill, like a
 * hand-placed length, so neither ever passes for a figure the scan measured.
 * Every figure is `@sts/geometry`'s `plan-measure.ts` over plan metres.
 */

const INERT = { pointerEvents: "none" } as const;

/** "90.0°", to a tenth of a degree. */
export function formatDegrees(deg: number): string {
  return `${deg.toFixed(1)}°`;
}

/** What a dimension reads, in words: "4.20 m", "90.0° · 3.00 m, 4.00 m", "12.00 m² · 14.00 m round". */
export function dimensionReading(dimension: Pick<PlanDimension, "start" | "end" | "points" | "kind">): string {
  const points = pointsOf(dimension);
  switch (kindOf(dimension)) {
    case "line":
      return formatLength(planDistanceM(dimension.start, dimension.end), "m");
    case "angle": {
      const [a, v, b] = points as [PlanPoint, PlanPoint, PlanPoint];
      const angle = planAngleDeg(a, v, b);
      return `${angle === undefined ? "—" : formatDegrees(angle)} · ${formatLength(planDistanceM(a, v), "m")}, ${formatLength(planDistanceM(v, b), "m")}`;
    }
    case "area":
      return `${formatArea(planPolygonAreaM2(points), "m")} · ${formatLength(planPathLengthM(points, true), "m")} round`;
  }
}

function Pill({
  at,
  text,
  interactive,
  onPick,
  onRemove,
}: {
  readonly at: { x: number; y: number };
  readonly text: string;
  readonly interactive: boolean;
  readonly onPick?: () => void;
  readonly onRemove?: () => void;
}): ReactNode {
  const w = Math.max(40, text.length * 7 + 16);
  return (
    <g>
      <rect
        className="floor-plan__pill"
        x={round(at.x - w / 2)}
        y={round(at.y - 10)}
        width={round(w)}
        height={20}
        rx={10}
        style={interactive ? { cursor: "pointer" } : undefined}
        onClick={
          interactive
            ? (event) => {
                event.stopPropagation();
                onPick?.();
              }
            : undefined
        }
      />
      <text className="floor-plan__pill-text" x={round(at.x)} y={round(at.y)} style={INERT}>
        {text}
      </text>
      {interactive && onRemove ? (
        <g
          className="floor-plan__dim-remove"
          data-testid="plan-dimension-remove"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
        >
          <title>Remove this dimension</title>
          <circle cx={round(at.x + w / 2 + 13)} cy={round(at.y)} r={9} />
          <path
            d={`M ${round(at.x + w / 2 + 9.5)},${round(at.y - 3.5)} l 7,7 M ${round(at.x + w / 2 + 16.5)},${round(at.y - 3.5)} l -7,7`}
          />
        </g>
      ) : null}
    </g>
  );
}

function LegLabel({ a, b, fit }: { readonly a: PlanPoint; readonly b: PlanPoint; readonly fit: PlanFit }): ReactNode {
  const va = toView(a, fit);
  const vb = toView(b, fit);
  const len = Math.hypot(vb.x - va.x, vb.y - va.y);
  if (len < 30) return null;
  const u = { x: (vb.x - va.x) / len, y: (vb.y - va.y) / len };
  const m = midpoint(va, vb);
  const at = { x: m.x - u.y * 12, y: m.y + u.x * 12 };
  const angle = uprightAngle(u);
  return (
    <text
      className="floor-plan__measure-leg"
      x={round(at.x)}
      y={round(at.y)}
      transform={`rotate(${round(angle)},${round(at.x)},${round(at.y)})`}
      style={INERT}
    >
      {formatLength(planDistanceM(a, b), "m")}
    </text>
  );
}

/**
 * An angle measurement: legs `points[0]`–`points[1]` and `points[1]`–`points[2]`,
 * an arc at the corner, each leg's length beside it and the angle in the pill.
 * With only two points (still being drawn) it is one leg.
 */
export function AngleMeasure({
  id,
  points,
  label,
  fit,
  selected = false,
  pending = false,
  interactive = false,
  onPick,
  onRemove,
}: {
  readonly id: string;
  readonly points: readonly PlanPoint[];
  readonly label?: string;
  readonly fit: PlanFit;
  readonly selected?: boolean;
  readonly pending?: boolean;
  readonly interactive?: boolean;
  readonly onPick?: (id: string) => void;
  readonly onRemove?: (id: string) => void;
}): ReactNode {
  const v = points.map((p) => toView(p, fit));
  const [a, vertex, b] = points;
  const angle = a && vertex && b ? planAngleDeg(a, vertex, b) : undefined;
  let arc: ReactNode = null;
  let pillAt = v[1] ?? v[0];
  if (v.length === 3 && angle !== undefined) {
    const [va, vv, vb] = v as [{ x: number; y: number }, { x: number; y: number }, { x: number; y: number }];
    const r = Math.min(26, Math.hypot(va.x - vv.x, va.y - vv.y) / 2, Math.hypot(vb.x - vv.x, vb.y - vv.y) / 2);
    const ua = { x: (va.x - vv.x) / Math.hypot(va.x - vv.x, va.y - vv.y), y: (va.y - vv.y) / Math.hypot(va.x - vv.x, va.y - vv.y) };
    const ub = { x: (vb.x - vv.x) / Math.hypot(vb.x - vv.x, vb.y - vv.y), y: (vb.y - vv.y) / Math.hypot(vb.x - vv.x, vb.y - vv.y) };
    const p1 = { x: vv.x + ua.x * r, y: vv.y + ua.y * r };
    const p2 = { x: vv.x + ub.x * r, y: vv.y + ub.y * r };
    const sweep = ua.x * ub.y - ua.y * ub.x > 0 ? 1 : 0;
    arc = <path className="floor-plan__measure-arc" fill="none" d={`M ${round(p1.x)},${round(p1.y)} A ${round(r)},${round(r)} 0 0 ${sweep} ${round(p2.x)},${round(p2.y)}`} />;
    // The figure sits out along the bisector, clear of both legs.
    const bis = { x: ua.x + ub.x, y: ua.y + ub.y };
    const bl = Math.hypot(bis.x, bis.y);
    const dir = bl > 1e-6 ? { x: bis.x / bl, y: bis.y / bl } : { x: -ua.y, y: ua.x };
    pillAt = { x: vv.x + dir.x * (r + 22), y: vv.y + dir.y * (r + 22) };
  }
  const text = angle === undefined ? "" : label ? `${label} · ${formatDegrees(angle)}` : formatDegrees(angle);
  return (
    <g
      className={["floor-plan__dim", "floor-plan__dim--manual", "floor-plan__measure--angle", selected ? "is-selected" : "", pending ? "is-pending" : ""].filter(Boolean).join(" ")}
      data-dimension-id={pending ? undefined : id}
      data-measure-kind="angle"
      style={interactive ? undefined : INERT}
    >
      {v.slice(1).map((p, i) => {
        const q = v[i] as { x: number; y: number };
        return <line key={i} className="floor-plan__dim-line" x1={round(q.x)} y1={round(q.y)} x2={round(p.x)} y2={round(p.y)} />;
      })}
      {arc}
      {points.slice(1).map((p, i) => (
        <LegLabel key={`leg-${i}`} a={points[i] as PlanPoint} b={p} fit={fit} />
      ))}
      {text && pillAt ? (
        <Pill
          at={pillAt}
          text={text}
          interactive={interactive}
          {...(onPick ? { onPick: () => onPick(id) } : {})}
          {...(onRemove ? { onRemove: () => onRemove(id) } : {})}
        />
      ) : null}
    </g>
  );
}

/**
 * An area measurement: the closed run shaded, its area and perimeter in the
 * pill at its middle. Still being drawn (`pending`), the run is closed back to
 * the first point with a dashed leg, and the figure is what it would enclose.
 */
export function AreaMeasure({
  id,
  points,
  label,
  fit,
  selected = false,
  pending = false,
  interactive = false,
  onPick,
  onRemove,
}: {
  readonly id: string;
  readonly points: readonly PlanPoint[];
  readonly label?: string;
  readonly fit: PlanFit;
  readonly selected?: boolean;
  readonly pending?: boolean;
  readonly interactive?: boolean;
  readonly onPick?: (id: string) => void;
  readonly onRemove?: (id: string) => void;
}): ReactNode {
  if (points.length < 2) return null;
  const v = points.map((p) => toView(p, fit));
  const d = `${v.map((p, i) => `${i === 0 ? "M" : "L"} ${round(p.x)},${round(p.y)}`).join(" ")}${points.length >= 3 ? " Z" : ""}`;
  const area = planPolygonAreaM2(points);
  const crossed = planPolygonSelfIntersects(points);
  const centre = toView(planCentroid(points), fit);
  const figure = points.length >= 3 ? `${formatArea(area, "m")}${crossed ? " (crosses itself)" : ""}` : "";
  const text = figure ? (label ? `${label} · ${figure}` : figure) : "";
  return (
    <g
      className={["floor-plan__dim", "floor-plan__dim--manual", "floor-plan__measure--area", selected ? "is-selected" : "", pending ? "is-pending" : ""].filter(Boolean).join(" ")}
      data-dimension-id={pending ? undefined : id}
      data-measure-kind="area"
      style={interactive ? undefined : INERT}
    >
      <path
        className="floor-plan__measure-area"
        d={d}
        style={interactive ? { cursor: "pointer" } : undefined}
        onClick={
          interactive
            ? (event) => {
                event.stopPropagation();
                onPick?.(id);
              }
            : undefined
        }
      />
      {text ? (
        <>
          <Pill
            at={centre}
            text={text}
            interactive={interactive}
            {...(onPick ? { onPick: () => onPick(id) } : {})}
            {...(onRemove ? { onRemove: () => onRemove(id) } : {})}
          />
          <text className="floor-plan__measure-leg" x={round(centre.x)} y={round(centre.y + 22)} style={INERT}>
            {`${formatLength(planPathLengthM(points, true), "m")} round`}
          </text>
        </>
      ) : null}
    </g>
  );
}
